// Shared labels + badge tones for the Onboarding section.

// GHL's statuses (our `new` = GHL "Unconfirmed") plus the cancel attribution
// split: GHL-originated cancellations arrive as "Canceled by customer",
// "Canceled by us" is set in the backoffice; both push "cancelled" to GHL.
export const CALL_STATUS_OPTIONS = [
  { value: 'new', label: 'New (Unconfirmed)' },
  { value: 'confirmed', label: 'Confirmed' },
  { value: 'show', label: 'Showed' },
  { value: 'no_show', label: 'No show' },
  { value: 'canceled_by_us', label: 'Canceled by us' },
  { value: 'canceled_by_customer', label: 'Canceled by customer' },
  { value: 'invalid', label: 'Invalid' },
] as const;

export const CALL_STATUS_LABELS: Record<string, string> = {
  ...Object.fromEntries(CALL_STATUS_OPTIONS.map((o) => [o.value, o.label])),
  // Legacy value on old rows; not selectable.
  rescheduled: 'Rescheduled',
};

export const CALL_STATUS_TONES: Record<string, 'success' | 'info' | 'warning' | 'danger' | 'neutral'> = {
  new: 'warning',
  confirmed: 'info',
  show: 'success',
  no_show: 'danger',
  canceled_by_us: 'neutral',
  canceled_by_customer: 'danger',
  invalid: 'neutral',
  // Legacy value
  rescheduled: 'info',
}

/** Source filter options + readable labels for the stored slugs. */
export const SOURCE_OPTIONS = [
  { value: 'onboarding', label: 'Onboarding' },
  { value: 'notification-bar', label: 'Notification bar' },
  { value: 'notification-bar-close-modal', label: 'Notification bar (close modal)' },
  { value: 'direct', label: 'Direct' },
  { value: 'ghl', label: 'GHL calendar' },
] as const

export const SOURCE_LABELS: Record<string, string> = Object.fromEntries(
  SOURCE_OPTIONS.map((o) => [o.value, o.label]),
)

/** Where the booking/decline happened — shown as a tooltip on the Source badge. */
export const SOURCE_DESCRIPTIONS: Record<string, string> = {
  onboarding: 'The /book offer shown right after finishing signup',
  'notification-bar': 'The "Book a call" link in the setup-offer notification bar',
  'notification-bar-close-modal': 'The confirm modal shown when closing the notification bar',
  direct: 'Visited /book without a recognized entry-point tag',
  ghl: 'Booked directly on the GHL Onboarding calendar and pulled in by the sync',
};

// ─── Outcome display ─────────────────────────────────────────────────────────
// The stored outcome is only 'booked' | 'skipped'. "Declined" is a display-only
// refinement of a skip that came from a notification-bar source (they engaged
// the paid offer, then backed out) — nothing new is stored, it's derived from
// outcome + source. A plain onboarding pass stays "Skipped".
export type OutcomeDisplay = 'booked' | 'declined' | 'skipped';

const NOTIFICATION_BAR_SOURCES = new Set(['notification-bar', 'notification-bar-close-modal']);

export function resolveOutcomeDisplay(outcome: string, source: string): OutcomeDisplay {
  if (outcome === 'booked') return 'booked';
  if (outcome === 'skipped' && NOTIFICATION_BAR_SOURCES.has(source)) return 'declined';
  return 'skipped';
}

export const OUTCOME_LABELS: Record<OutcomeDisplay, string> = {
  booked: 'Booked',
  declined: 'Declined',
  skipped: 'Skipped',
};

export const OUTCOME_TONES: Record<OutcomeDisplay, 'success' | 'warning' | 'neutral'> = {
  booked: 'success',
  declined: 'warning',
  skipped: 'neutral',
};

export const OUTCOME_DESCRIPTIONS: Record<OutcomeDisplay, string> = {
  booked: 'Booked a setup call',
  declined: 'Came to book a call, then changed their mind',
  skipped: 'Passed on the offer without booking',
};
